import type { ItemFieldRow } from './item-field-reader';
import { readInventoryTemplateId } from './item-field-reader';
import { createInventoryReadContext } from './inventory-read-context';
import type { InventoryReadContext } from './inventory-read-context';
import { phpJsonString } from './php-json-values';
import { utf8Bytes } from './php-json-serialize';

const arrayLike = (value: unknown): value is ItemFieldRow => value !== null && typeof value === 'object';
const own = (value: unknown,key: string): unknown => arrayLike(value) && Object.prototype.propertyIsEnumerable.call(value,key)
    ? (value as Readonly<Record<string,unknown>>)[key] : undefined;
export class InventoryEconomyNonJsonResult extends Error {}
function nullableOffset(value: unknown,key: string): unknown {
    if (typeof value !== 'string') return own(value,key);
    if (!/^[\t\n\r\v\f ]*[+-]?\d+[\t\n\r\v\f ]*$/.test(key)) return undefined;
    const integer = BigInt(key.trim());
    if (integer < -9223372036854775808n || integer > 9223372036854775807n) return undefined;
    const bytes = utf8Bytes(value), index = integer < 0n ? BigInt(bytes.length)+integer : integer;
    if (index < 0n || index >= BigInt(bytes.length)) return undefined;
    const byte = bytes[Number(index)]!;
    if (byte >= 128) throw new InventoryEconomyNonJsonResult('PHP byte offset is not a JSON-encodable UTF-8 string');
    return String.fromCharCode(byte);
}
function clone(value: unknown): unknown {
    if (!arrayLike(value)) return value ?? null;
    const entries = Object.entries(value);
    return entries.every(([key],i)=>key===String(i)) ? entries.map(([,child])=>clone(child))
        : Object.fromEntries(entries.map(([key,child])=>[key,clone(child)]));
}
export interface InventoryEconomyReaderDependencies {
    resolveInventoryInstance: (id: string) => ItemFieldRow | null;
    inventoryContext?: InventoryReadContext;
    legacyRead?: (path: 'cost' | 'weight') => void;
}
export type InventoryEconomyReader = (row: ItemFieldRow | null,bucket: 'cost' | 'weight',key: string) => unknown;
/** computed_value's complete cost/weight branch. A missing instance value can
 * resolve from its riding template catalog, then the shared full-instance bridge.
 * This reads sparse cached economy values; it does not derive or rebuild them.
 */
export function createInventoryEconomyReader(deps: InventoryEconomyReaderDependencies): InventoryEconomyReader {
    const context = deps.inventoryContext ?? createInventoryReadContext(deps.resolveInventoryInstance);
    const read: InventoryEconomyReader = (row,bucketName,key) => {
        if (key == null || arrayLike(key)) throw new TypeError('Computed key requires a scalar string');
        key = phpJsonString(key);
        if (bucketName !== 'cost' && bucketName !== 'weight') throw new TypeError('Economy reader supports cost and weight only');
        if (row === null) return null;
        if (!arrayLike(row)) throw new TypeError('Computed reader requires a PHP array or null');
        const computed = own(own(row,'computed'),bucketName), legacy = own(row,bucketName);
        let bucket: unknown = null;
        if (arrayLike(computed)) bucket = computed;
        else if (arrayLike(legacy)) { deps.legacyRead?.(bucketName); bucket = legacy; }
        let value: unknown = own(bucket,key) ?? null;
        if (value === null) {
            const id = readInventoryTemplateId(row);
            if (id !== null) {
                const catalog = own(row,'catalog'), definition = own(catalog,id) ?? own(catalog,id.toUpperCase()) ?? own(catalog,id.toLowerCase());
                value = arrayLike(definition) ? nullableOffset(own(own(definition,'computed'),bucketName),key) ?? null : null;
                if (value === null) value = context.read(row,full=>read(full,bucketName,key));
            }
        }
        if (arrayLike(value) && Object.prototype.propertyIsEnumerable.call(value,'0')) value = own(value,'0');
        return clone(value);
    };
    return read;
}
